import {Reel} from 'com/gongxigames/math/core/game/Reel';
import {Container} from 'pixi.js';
import {AbstractReelsView} from '@gongxigames/framework/slot/modules/reels/AbstractReelsView';
import {ReelsViewEvents} from '@gongxigames/framework/slot/modules/reels/ReelsViewEvents';
import {ReelView, ReelViewData} from '@gongxigames/framework/slot/modules/reels/ReelView';
import {consoleLogger} from '@gongxigames/framework/core';

export abstract class IndependentReelsView extends AbstractReelsView {
    public static readonly NAME: symbol = Symbol('IndependentReelsView');

    protected createReelSet(reels: Reel[], reelsPosition: number[]): void {
        this._symbolsPool.freeAll();
        this.setupReelsLayers();

        this.iterateReels(reels.length, (reelIndex) => {
            const reelData = this.getReelData(reelIndex);

            if (reelsPosition && reelsPosition[reelIndex]) {
                reelData.startPosition = reelsPosition[reelIndex];
            }

            const strip = reels[reelIndex].getSymbols().map(symbol => {
                return symbol === this.getBlankSymbol() ? this.getSymbolInsteadBlank() : symbol;
            });

            const reel = this.createReel(reelData);

            reel.strip = strip;
            reel.init(this.objectBuilder, this.spineUtils);

            reel.onReelStopping = this.onReelStopping.bind(this);
            reel.onReelStopped = this.onReelStopped.bind(this);
            reel.onReelAnimationFinished = this.onReelAnimationFinished.bind(this);
            reel.onBounceStoppingStartedCallback = this.onBounceStoppingStarted.bind(this);

            this.updateManager.add(reel);
            reel.start();
            this._reels.push(reel);
        });

        this.setReelsBackground();
        this.forceUpdateOrientation();
    }

    /**
     * This function setups default reels layers
     * Should be overridden if need to add additional layers
     */
    protected setupReelsLayers(): void {
        for (const reel of this._reels) {
            this.updateManager.remove(reel);
        }

        this._reels = [];
        this.container.removeChildren();

        this._reelsetContainer = new Container();
        this._reelSetAnimationContainer = new Container();
        this.container.addChild(this._reelsetContainer);
        this.container.addChild(this._reelSetAnimationContainer);
    }

    /**
     * This function returns default reel data
     * Should be overridden if need to add additional reel data
     */
    protected getReelData(reelIndex: number): ReelViewData {
        return {
            index: reelIndex,
            startPosition: 0,
            reelSpeed: this.reelConstants.REEL_SPEED_MULTIPLIER,
            reelsContainer: this._reelsetContainer,
            animationContainer: this._reelSetAnimationContainer,
            offsets: {
                x: this.reelConstants.REELS[reelIndex].x,
                y: this.reelConstants.REELS[reelIndex].y
            },
            reelsConfig: this._gameConfig.reelsConfig,
            symbolsPool: this._symbolsPool,
            reelConstants: this.reelConstants
        } as ReelViewData;
    }

    public reOrder(): void {
    }

    public setReelsBackground(): void {
    }

    protected onReelAnimationFinished(_reelNr: number): void {
    }

    protected onBounceStoppingStarted(_reelNr: number): void {
    }

    protected onReelStopping(reelNr: number, distanceBeforeStop: number): void {
        this.playReelRelatedSound(reelNr);

        this.emit(ReelsViewEvents.REEL_STOPPING_CALLBACK, reelNr, distanceBeforeStop);
    }

    protected playReelRelatedSound(_reelNR: number): void {
    }

    protected onReelStopped(reelNr: number): void {
        this.reOrder();
        this.putOversizedsOnSpecialLayer(reelNr);
        this.emit(ReelsViewEvents.REEL_STOPPED, reelNr);
        consoleLogger.log('Independent reel stopped: ', reelNr);

        const allReelsStopped = this._reels.every((reel) => {
            return reel.isStopped;
        });

        if (allReelsStopped) {
            this.onLastReelStopped();
        }
    }

    protected onLastReelStopped(): void {
        this.playLastReelStopSound();
        this.emit(ReelsViewEvents.ALL_REELS_STOPPED);
    }

    protected playLastReelStopSound(): void {
    }

    protected removeOversizedsFromSpecialLayer(): void {
    }

    protected setupReelSetMaskForSpinning(): void {
    }

    public onOrientationChanged(): void {
        this._reelsScaleService.scaleByCurrentScreenOrientation(this._reelsetContainer);
        this._reelsScaleService.scaleByCurrentScreenOrientation(this._oversizedsOnStoppedReelsLayer);
        this._reelsScaleService.scaleByCurrentScreenOrientation(this._reelSetAnimationContainer);
    }

    protected abstract createReel(reelData: ReelViewData): ReelView;

    protected abstract getBlankSymbol(): number;

    protected abstract getSymbolInsteadBlank(): number;
}
